import React, { useContext, useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AccordionContext, AccordionGroupContext } from '../../context';
import Icon from '../../../icon';
import { APPEARANCES } from '../../constants';

const ICON_CLASS = 'ws-ui-accordion-header__icon';

const AccordionHeader = ({ title, isSticky = false }) => {
  const { activeItems, toggleItem, appearance } = useContext(AccordionContext);
  const itemId = useContext(AccordionGroupContext);
  const ref = useRef();
  const sentinelRef = useRef();

  const [iconClasses, setIconClasses] = useState(ICON_CLASS);

  const isActive = activeItems.includes(itemId);
  const classes = classNames('ws-ui-accordion-header', `ws-ui-accordion-header--${appearance}`, {
    'ws-ui-accordion-header--active': isActive,
    'ws-ui-accordion-header--sticky': isSticky,
  });

  const handleClick = useCallback(() => toggleItem(itemId), [itemId, toggleItem]);

  /*
   * If accordion has active items on first render,
   * we need to prevent header icon animation
   */
  useEffect(() => {
    setTimeout(() => {
      setIconClasses(classNames(ICON_CLASS, `${ICON_CLASS}--with-animation`));
    }, 0);
  }, []);

  useEffect(() => {
    const sentinelElement = sentinelRef.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        ref.current.classList.toggle('ws-ui-accordion-header--pinned', e.intersectionRatio === 0);
      },
      { threshold: [0, 1] }
    );

    if (sentinelElement) {
      observer.observe(sentinelElement);
    }

    return () => {
      if (sentinelElement) {
        observer.unobserve(sentinelElement);
      }
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {isSticky && appearance !== APPEARANCES.default && <div ref={sentinelRef} />}
      <button ref={ref} type="button" className={classes} onClick={handleClick}>
        <span className="ws-ui-accordion-header__title">{title}</span>
        <span className={iconClasses}>
          <Icon glyph="down-small" type="default" />
        </span>
      </button>
    </React.Fragment>
  );
};

AccordionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  isSticky: PropTypes.bool,
};

export default AccordionHeader;
